import { Component, Host, h, Prop, Watch } from '@stencil/core';
export class ProgressSpinner {
  constructor() {
    this.transition = 'none';
    /**
     * State of the progress spinner: 'determinate' means that the percentage of completion is known and the circle filled portion will be defined by 'value',
     * 'indeterminate' means that the percentage is unknown and the circle will play a looping animation.
     */
    this.type = 'determinate';
    /**
     * Radius of the progress spinner circle in pixels.
     */
    this.radius = 100;
    /**
     * Stroke width in percentage of the progress spinner outer circle. Value must be between 1 and 50 (both included).
     */
    this.strokeWidth = 10;
    /**
     * Defines if the progress spinner will have a background circle.
     */
    this.hasBackground = false;
  }
  fixValue() {
    this.value = this.value >= 0 ? (this.value <= 100 ? this.value : 100) : 0;
    this.value = this.type === 'indeterminate' ? 40 : this.value;
  }
  getStrokeWidth() {
    return this.strokeWidth < 1 ? 1 : this.strokeWidth > 50 ? 50 : this.strokeWidth;
  }
  getInnerRadius() {
    return (100 - this.getStrokeWidth()) / 2;
  }
  getRadius() {
    return 2 * this.radius * (this.getInnerRadius() / 100.0);
  }
  getCircumference() {
    return 2 * Math.PI * this.getRadius();
  }
  getStrokeDashoffset() {
    return (this.getCircumference() * (100 - this.value)) / 100;
  }
  getBackgroundCircleStyle() {
    const style = {
      strokeWidth: this.getStrokeWidth() + '%',
    };
    return style;
  }
  getCircleStyle() {
    const style = {
      strokeDashoffset: this.getStrokeDashoffset() + 'px',
      strokeDasharray: this.getCircumference() + 'px',
      strokeWidth: this.getStrokeWidth() + '%',
      transition: this.transition,
    };
    return style;
  }
  lockTransition() {
    this.transition = 'none';
  }
  unlockTransition() {
    this.transition = this.type === 'determinate' ? 'stroke-dashoffset .6s ease' : 'none';
  }
  /**
   * Adjusts invalid values and sets a fixed value if type is indeterminate
   */
  validateValueWatcher() {
    this.fixValue();
  }
  /**
   * Locks the transition temporarily to prevent errors when changing the circle radius
   */
  transitionWatcher() {
    this.lockTransition();
  }
  componentWillLoad() {
    this.fixValue();
  }
  componentDidRender() {
    this.unlockTransition();
  }
  render() {
    return (h(Host, null,
      h("svg", { width: `${2 * this.radius}`, height: `${2 * this.radius}` },
        this.hasBackground ? h("circle", { class: "progress-spinner-background", cx: "50%", cy: "50%", r: `${this.getInnerRadius()}%`, style: this.getBackgroundCircleStyle() }) : '',
        h("circle", { class: "progress-spinner-circle", cx: "50%", cy: "50%", r: `${this.getInnerRadius()}%`, style: this.getCircleStyle() }))));
  }
  static get is() { return "sy-progress-spinner"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["scss/index.scss", "scss/_tokens.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["./scss/index.css", "./scss/_tokens.css"]
  }; }
  static get properties() { return {
    "value": {
      "type": "number",
      "mutable": true,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Percentage of the circle that must be active. Must be a value between 0 and 100. Only applied if the 'type' is 'determinate'."
      },
      "attribute": "value",
      "reflect": false
    },
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'determinate' | 'indeterminate'",
        "resolved": "\"determinate\" | \"indeterminate\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "State of the progress spinner: 'determinate' means that the percentage of completion is known and the circle filled portion will be defined by 'value',\n'indeterminate' means that the percentage is unknown and the circle will play a looping animation."
      },
      "attribute": "type",
      "reflect": true,
      "defaultValue": "'determinate'"
    },
    "radius": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Radius of the progress spinner circle in pixels."
      },
      "attribute": "radius",
      "reflect": true,
      "defaultValue": "100"
    },
    "strokeWidth": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Stroke width in percentage of the progress spinner outer circle. Value must be between 1 and 50 (both included)."
      },
      "attribute": "stroke-width",
      "reflect": true,
      "defaultValue": "10"
    },
    "hasBackground": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Defines if the progress spinner will have a background circle."
      },
      "attribute": "has-background",
      "reflect": true,
      "defaultValue": "false"
    }
  }; }
  static get watchers() { return [{
      "propName": "value",
      "methodName": "validateValueWatcher"
    }, {
      "propName": "type",
      "methodName": "validateValueWatcher"
    }, {
      "propName": "radius",
      "methodName": "transitionWatcher"
    }, {
      "propName": "strokeWidth",
      "methodName": "transitionWatcher"
    }]; }
}
